import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Zap,
  X,
  ArrowRight,
  Bell,
  FlaskConical,
  Key,
  ShieldCheck,
  Lock,
  ArrowUpDown,
  Tag,
  Layers,
} from 'lucide-react';
import { LoadingState } from '../components/ui/LoadingState';
import { EmptyState } from '../components/ui/EmptyState';
import type { Product, Plan } from '../types';

export interface ProductWithPlans extends Product {
  plans: Plan[];
}

interface CatalogMeta {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface Notification {
  id: string;
  type: 'new_product' | 'new_endpoint';
  title: string;
  message: string;
  product_id: string;
  product_name: string;
  created_at: string;
}

export const CATEGORIES = [
  { id: 'all', label: 'Todas as Categorias' },
  { id: 'fintech', label: 'Fintech & Pagamentos' },
  { id: 'gov', label: 'Governo & Fiscalidade' },
  { id: 'telecom', label: 'Telecom & Mensagens' },
  { id: 'utility', label: 'Utilitários & Core' },
] as const;

export const AUTH_TYPES = [
  { id: 'all', label: 'Todos os Métodos' },
  { id: 'API_KEY', label: 'API Key' },
  { id: 'OAUTH2', label: 'OAuth 2.0' },
  { id: 'BASIC_AUTH', label: 'Basic Auth' },
  { id: 'HMAC', label: 'HMAC' },
] as const;

export const TIERS = ['all', 'basic', 'business', 'enterprise'] as const;

export type SortOption = 'recent' | 'name_asc' | 'name_desc' | 'price_asc';

export function resolveProductCategory(product: {
  name: string;
  description?: string;
  category?: string;
}): string {
  if (product.category) return product.category.toLowerCase();
  const text = `${product.name} ${product.description || ''}`.toLowerCase();
  if (/rupe|sigfe|imposto|fiscal|tribut|agt|governo|identidade/.test(text)) {
    return 'gov';
  }
  if (
    /pagamento|fintech|banco|wallet|carteira|kwanza|emis|multicaixa|cartão|transferência|credit|transação/.test(
      text
    )
  ) {
    return 'fintech';
  }
  if (/sms|telecom|mensagem|notificação|otp|whatsapp|telefonia/.test(text)) {
    return 'telecom';
  }
  return 'utility';
}

export function getAuthBadge(provider?: string) {
  switch (provider) {
    case 'OAUTH2':
      return {
        label: 'OAuth 2.0',
        icon: ShieldCheck,
        color:
          'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-400 dark:border-indigo-800/60',
      };
    case 'BASIC_AUTH':
      return {
        label: 'Basic Auth',
        icon: Lock,
        color:
          'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/60',
      };
    case 'HMAC':
      return {
        label: 'HMAC Sign',
        icon: ShieldCheck,
        color:
          'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-400 dark:border-purple-800/60',
      };
    case 'API_KEY':
    default:
      return {
        label: 'API Key',
        icon: Key,
        color:
          'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800/60',
      };
  }
}

export function getLowestPrice(plans?: Plan[]): { isFree: boolean; minPrice: number } {
  if (!plans || plans.length === 0) return { isFree: false, minPrice: 0 };
  const prices = plans.map((p) => Number(p.monthly_price) || 0);
  const min = Math.min(...prices);
  return { isFree: min === 0, minPrice: min };
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export const Catalog: React.FC = () => {
  const { id: appId } = useParams<{ id: string }>();
  const client = useAuthStore((state) => state.client);

  const [searchInput, setSearchInput] = useState('');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterAuth, setFilterAuth] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortOption>('recent');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('dismissed_notifs') || '[]'));
    } catch {
      return new Set();
    }
  });

  const search = useDebounce(searchInput, 350);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, filterTier, filterCategory, filterAuth, sortBy, pageSize]);

  // ── Products query (server-side search + pagination) ──────────────────────
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['products', search, filterTier, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterTier !== 'all') params.set('tier', filterTier);
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      const response = await api.get<{ data: ProductWithPlans[]; meta: CatalogMeta }>(
        `/products?${params.toString()}`
      );
      return response.data;
    },
    placeholderData: (prev) => prev,
  });

  const rawProducts = data?.data ?? [];
  const meta = data?.meta;

  // Client-side category, auth, and sorting filtering on returned dataset
  const filteredProducts = useMemo(() => {
    let result = [...rawProducts];

    // Category filter
    if (filterCategory !== 'all') {
      result = result.filter((p) => resolveProductCategory(p) === filterCategory);
    }

    // Auth method filter
    if (filterAuth !== 'all') {
      result = result.filter(
        (p) => (p.authentication_provider || 'API_KEY').toUpperCase() === filterAuth
      );
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === 'name_asc') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'name_desc') {
        return b.name.localeCompare(a.name);
      }
      if (sortBy === 'price_asc') {
        const priceA = getLowestPrice(a.plans).minPrice;
        const priceB = getLowestPrice(b.plans).minPrice;
        return priceA - priceB;
      }
      // 'recent' by default
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });

    return result;
  }, [rawProducts, filterCategory, filterAuth, sortBy]);

  // ── Notifications polling (every 30s) ─────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const params = new URLSearchParams({ since });
      if (client?.id) params.set('client_id', client.id);
      const res = await api.get<{ notifications: Notification[] }>(
        `/notifications/catalog?${params.toString()}`
      );
      setNotifications(res.data.notifications.filter((n) => !dismissedIds.has(n.id)));
    } catch {
      /* non-fatal */
    }
  }, [client?.id, dismissedIds]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const dismiss = (id: string) => {
    const next = new Set(dismissedIds).add(id);
    setDismissedIds(next);
    localStorage.setItem('dismissed_notifs', JSON.stringify([...next]));
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const dismissAll = () => {
    const next = new Set([...dismissedIds, ...notifications.map((n) => n.id)]);
    setDismissedIds(next);
    localStorage.setItem('dismissed_notifs', JSON.stringify([...next]));
    setNotifications([]);
  };

  const visibleNotifications = notifications.slice(0, 3);

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1 flex items-center gap-2.5">
            <Layers className="w-7 h-7 text-primary-500" />
            <span>Catálogo de APIs</span>
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
            {meta
              ? `${meta.total} produto${meta.total !== 1 ? 's' : ''} disponíve${meta.total !== 1 ? 'is' : 'l'} no Gateway oficial`
              : 'Navegue, explore a documentação interativa e assine as APIs para a sua aplicação'}
          </p>
        </div>

        {/* Sort Dropdown */}
        <div className="flex items-center gap-2 shrink-0">
          <ArrowUpDown className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
            Ordenar por:
          </span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-1.5 text-xs font-medium focus:ring-2 focus:ring-primary-500 outline-hidden"
          >
            <option value="recent">Mais Recentes</option>
            <option value="name_asc">Nome (A - Z)</option>
            <option value="name_desc">Nome (Z - A)</option>
            <option value="price_asc">Menor Preço Inicial</option>
          </select>
        </div>
      </div>

      {/* Notifications banner */}
      {visibleNotifications.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 uppercase tracking-wider">
              <Bell className="w-3.5 h-3.5 text-primary-500" />
              <span>
                {notifications.length} novidade{notifications.length !== 1 ? 's' : ''} no catálogo
              </span>
            </span>
            <button
              onClick={dismissAll}
              className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline"
            >
              Dispensar todas
            </button>
          </div>
          {visibleNotifications.map((n) => (
            <div
              key={n.id}
              className={`flex items-start justify-between gap-3 p-3.5 rounded-xl border transition-all ${
                n.type === 'new_product'
                  ? 'bg-primary-50/80 dark:bg-primary-950/30 border-primary-200 dark:border-primary-800/60'
                  : 'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60'
              }`}
            >
              <div className="flex items-start gap-2.5 flex-1">
                <div
                  className={`p-1.5 rounded-lg shrink-0 mt-0.5 ${
                    n.type === 'new_product'
                      ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400'
                      : 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400'
                  }`}
                >
                  {n.type === 'new_product' ? (
                    <Sparkles className="w-4 h-4" />
                  ) : (
                    <Zap className="w-4 h-4" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{n.title}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                    {n.message} · {new Date(n.created_at).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 shrink-0">
                <Link
                  to={appId ? `/applications/${appId}/products/${n.product_id}` : '/applications'}
                  className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Explorar
                </Link>
                <button
                  onClick={() => dismiss(n.id)}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  title="Dispensar notificação"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {notifications.length > 3 && (
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              +{notifications.length - 3} mais notificações
            </p>
          )}
        </div>
      )}

      {/* Categories Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setFilterCategory(cat.id)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
              filterCategory === cat.id
                ? 'bg-primary-600 text-white shadow-xs'
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
          >
            <Tag className="w-3.5 h-3.5" />
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Search + Granular Filters Card */}
      <div className="bg-white dark:bg-slate-900 p-4.5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Pesquisar APIs por nome, finalidade ou descrição (ex: RUPE, SIGFE, Pagamentos)..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
              aria-label="Pesquisar APIs"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                title="Limpar pesquisa"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Auth Method Selector */}
          <div className="flex items-center gap-2">
            <select
              value={filterAuth}
              onChange={(e) => setFilterAuth(e.target.value)}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2.5 text-xs font-medium focus:ring-2 focus:ring-primary-500 outline-hidden"
            >
              {AUTH_TYPES.map((auth) => (
                <option key={auth.id} value={auth.id}>
                  {auth.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tier filter pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {TIERS.map((tier) => (
              <button
                key={tier}
                onClick={() => setFilterTier(tier)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filterTier === tier
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-2xs font-bold'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {tier === 'all' ? 'Todos os Planos' : tier.charAt(0).toUpperCase() + tier.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Active filters summary */}
        {(search || filterTier !== 'all' || filterCategory !== 'all' || filterAuth !== 'all') && (
          <div className="flex items-center gap-2 pt-3 border-t border-slate-200/70 dark:border-slate-800 text-xs flex-wrap">
            <span className="text-slate-500 dark:text-slate-400 font-medium">Filtros activos:</span>
            {search && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                "{search}"
                <button
                  onClick={() => setSearchInput('')}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filterCategory !== 'all' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                Categoria:{' '}
                {CATEGORIES.find((c) => c.id === filterCategory)?.label || filterCategory}
                <button
                  onClick={() => setFilterCategory('all')}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filterAuth !== 'all' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                Autenticação: {filterAuth}
                <button
                  onClick={() => setFilterAuth('all')}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filterTier !== 'all' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                Plano: {filterTier}
                <button
                  onClick={() => setFilterTier('all')}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setSearchInput('');
                setFilterTier('all');
                setFilterCategory('all');
                setFilterAuth('all');
              }}
              className="text-primary-600 dark:text-primary-400 hover:underline ml-auto font-semibold"
            >
              Limpar tudo
            </button>
          </div>
        )}
      </div>

      {/* Products Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <LoadingState type="card" count={6} />
        </div>
      ) : filteredProducts.length > 0 ? (
        <>
          {/* Fetching overlay */}
          <div className={`transition-opacity ${isFetching ? 'opacity-60' : 'opacity-100'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => {
                const authInfo = getAuthBadge(product.authentication_provider);
                const AuthIcon = authInfo.icon;
                const priceInfo = getLowestPrice(product.plans);
                const categoryId = resolveProductCategory(product);
                const categoryLabel =
                  CATEGORIES.find((c) => c.id === categoryId)?.label || 'API Core';

                return (
                  <div
                    key={product.id}
                    className="bg-white dark:bg-slate-900 p-6 flex flex-col rounded-2xl border border-slate-200 dark:border-slate-800 transition-all hover:shadow-lg hover:-translate-y-0.5 group relative"
                  >
                    {/* Top Badges Row */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-2xs font-bold border ${authInfo.color}`}
                      >
                        <AuthIcon className="w-3 h-3" />
                        <span>{authInfo.label}</span>
                      </span>

                      <div className="flex items-center gap-1.5">
                        <span className="text-2xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                          {categoryLabel.split(' ')[0]}
                        </span>
                        {product.deprecated && (
                          <span className="badge-modern badge-warning shrink-0 text-xs">
                            Descontinuado
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Product title & version */}
                    <div className="mb-2">
                      <Link
                        to={
                          appId ? `/applications/${appId}/products/${product.id}` : '/applications'
                        }
                        className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors block"
                      >
                        {product.name}
                      </Link>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-4 flex-1 line-clamp-3 leading-relaxed">
                      {product.description}
                    </p>

                    {/* Pricing Highlight Banner */}
                    <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 mb-4 flex items-center justify-between text-xs">
                      <span className="text-2xs text-slate-500 uppercase font-semibold">
                        Preço Base
                      </span>
                      <span className="font-bold text-slate-900 dark:text-white font-mono">
                        {priceInfo.isFree ? (
                          <span className="text-emerald-600 dark:text-emerald-400">
                            Grátis (Plano Básico)
                          </span>
                        ) : (
                          `A partir de $${priceInfo.minPrice}/mês`
                        )}
                      </span>
                    </div>

                    {/* Plans Tags */}
                    <div className="border-t border-slate-100 dark:border-slate-800 pt-3 mb-4">
                      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                        Níveis Disponíveis
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {product.plans && product.plans.length > 0 ? (
                          product.plans.map((plan) => (
                            <span
                              key={plan.id}
                              className={`badge-modern text-xs ${
                                plan.tier === 'basic'
                                  ? 'badge-primary'
                                  : plan.tier === 'business'
                                    ? 'badge-success'
                                    : 'badge-warning'
                              }`}
                            >
                              {plan.tier.charAt(0).toUpperCase() + plan.tier.slice(1)}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            Sem planos configurados
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons: Sandbox Tester + Explore */}
                    <div className="grid grid-cols-2 gap-2 mt-auto pt-2 border-t border-slate-100 dark:border-slate-800/80">
                      <Link
                        to={
                          appId
                            ? `/applications/${appId}/products/${product.id}?tab=tester`
                            : '/applications'
                        }
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/60 border border-primary-200/60 dark:border-primary-800/60 transition-colors"
                        title="Abrir Console de Testes Sandbox com credenciais desta aplicação"
                      >
                        <FlaskConical className="w-3.5 h-3.5" />
                        <span>Testar Sandbox</span>
                      </Link>

                      <Link
                        to={
                          appId ? `/applications/${appId}/products/${product.id}` : '/applications'
                        }
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 transition-colors"
                      >
                        <span>Documentação</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination Controls */}
          {meta && meta.total > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center gap-4 flex-wrap">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  A mostrar{' '}
                  <span className="font-bold text-slate-900 dark:text-white">
                    {(meta.page - 1) * meta.limit + 1}
                  </span>{' '}
                  a{' '}
                  <span className="font-bold text-slate-900 dark:text-white">
                    {Math.min(meta.page * meta.limit, meta.total)}
                  </span>{' '}
                  de <span className="font-bold text-slate-900 dark:text-white">{meta.total}</span>{' '}
                  produtos
                </p>

                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span>Por página:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white px-2 py-1 text-xs focus:ring-2 focus:ring-primary-500 outline-none"
                  >
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                  </select>
                </div>
              </div>

              {meta.total_pages > 1 && (
                <div className="flex items-center gap-1.5">
                  {/* Previous */}
                  <button
                    onClick={() => {
                      setPage((p) => p - 1);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={!meta.has_prev}
                    className="px-3.5 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="w-4 h-4" /> Anterior
                  </button>

                  {/* Next */}
                  <button
                    onClick={() => {
                      setPage((p) => p + 1);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={!meta.has_next}
                    className="px-3.5 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                    aria-label="Próxima página"
                  >
                    Próxima <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <EmptyState
          title="Nenhuma API encontrada"
          description="Tente ajustar a sua pesquisa ou remover filtros para visualizar outras APIs disponíveis."
          action={
            <button
              onClick={() => {
                setSearchInput('');
                setFilterTier('all');
                setFilterCategory('all');
                setFilterAuth('all');
              }}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-semibold transition-colors"
            >
              Limpar Filtros de Pesquisa
            </button>
          }
        />
      )}
    </div>
  );
};
